import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Power, Eye } from 'lucide-react';
import type { BrowserInstance } from '@/types';
import { setInstanceEnabled } from '@/api/real-browsers';
import { relativeTime, pct } from '@/lib/format';
import { cn } from '@/lib/cn';

interface Props { instance: BrowserInstance; }

const STATUS_DOT: Record<BrowserInstance['status'], string> = {
  healthy:  'bg-emerald-500',
  degraded: 'bg-yellow-500',
  dead:     'bg-red-500',
  unknown:  'bg-slate-500',
};

export default function InstanceCard({ instance }: Props) {
  const qc = useQueryClient();

  const toggleM = useMutation({
    mutationFn: (enabled: boolean) => setInstanceEnabled(instance.id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances'] }),
  });

  return (
    <div className={cn(
      'bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex flex-col gap-3',
      !instance.enabled && 'opacity-60',
    )}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn('dot shrink-0', STATUS_DOT[instance.status])} />
            <span className="font-medium font-mono text-sm truncate" title={instance.id}>
              :{instance.cdp_port}
            </span>
            <span className="text-xs text-slate-500 truncate">
              {instance.cdp_host}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1 truncate">{instance.id}</p>
        </div>
        <button
          onClick={() => toggleM.mutate(!instance.enabled)}
          disabled={toggleM.isPending}
          className={cn(
            'tap-target rounded-md transition',
            instance.enabled
              ? 'text-emerald-400 hover:bg-emerald-500/10'
              : 'text-slate-500 hover:bg-slate-800',
          )}
          aria-label={instance.enabled ? 'Pause instance' : 'Resume instance'}
          title={instance.enabled ? 'Pause' : 'Resume'}
        >
          <Power className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <Stat label="status" value={instance.status} />
        <Stat label="active tabs" value={`${instance.active_tabs}/${instance.concurrent_tabs}`} />
        <Stat label="block 24h" value={pct(instance.block_rate_24h)} />
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>last seen: {relativeTime(instance.last_heartbeat_at)}</span>
        <Link
          to="/live/$instanceId"
          params={{ instanceId: instance.id }}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-500/10 text-sky-300 ring-1 ring-sky-500/30 hover:bg-sky-500/20 transition"
        >
          <Eye className="w-3.5 h-3.5" />
          Open Live
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-slate-950/60 rounded-md px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm font-mono text-slate-200 truncate">{value}</div>
    </div>
  );
}
